// src/pages/categorias/NuevaCategoria.js
import React, { useState, useEffect } from 'react'
import Row from 'react-bootstrap/Row'
import Col from 'react-bootstrap/Col'
import Form from 'react-bootstrap/Form'
import Button from 'react-bootstrap/Button'
import { FormLabel, Offcanvas, Alert } from 'react-bootstrap'
import { PanelFooter, PanelHeader } from 'components/Panel'
import FileUpload from 'components/FileUpload/FileUpload'
import AtributosSelector from 'components/AtributosSelector'
import { useForm } from 'hooks/useForm'
import api from 'api/api'

function NuevaCategoria(props) {
	const { mostrar, categoriasDisponibles, guardar, cancelar, defaultImage } = props

	const [erroresValidacion, setErroresValidacion] = useState({})
	const [loading, setLoading] = useState(false)

	// Estados para atributos
	const [atributosTiposDisponibles, setAtributosTiposDisponibles] = useState([])
	const [atributosTipoIdsSeleccionados, setAtributosTipoIdsSeleccionados] = useState([])
	const [loadingAtributos, setLoadingAtributos] = useState(false)

	// Preparar opciones para categorías padre
	const opcionesCategorias = [
		{ value: '', label: 'Categoría principal' },
		...categoriasDisponibles.map(cat => ({
			value: cat.id,
			label: cat.nombre
		}))
	]

	const datosIniciales = {
		nombre: '',
		jerarquiaPapaId: '',
		archivo: null
	}

	// Cargar tipos de atributos disponibles
	const cargarAtributosTipos = async () => {
		try {
			setLoadingAtributos(true)
			const { datos } = await api.get('a/config-atributos')
			setAtributosTiposDisponibles(datos.tipos || [])
		} catch (error) {
			console.error('Error cargando tipos de atributos:', error)
		} finally {
			setLoadingAtributos(false)
		}
	}

	const handleClose = () => {
		setAtributosTipoIdsSeleccionados([])
		cancelar()
	}

	useEffect(() => {
		if (mostrar) {
			cargarAtributosTipos()
		} else {
			reset()
			setAtributosTipoIdsSeleccionados([])
		}
	}, [mostrar])

	const handleProcesar = async (datos) => {
		if (validarDatos()) {
			const datosCategoria = {
				nombre: datos.nombre,
				jerarquiaPapaId: datos.jerarquiaPapaId || null,
				archivo: datos.archivo,
				// Incluir los atributos seleccionados
				atributosTipoIds: atributosTipoIdsSeleccionados
			}

			setLoading(true)
			await guardar(datosCategoria)
			setLoading(false)
			handleReset()
		}
	}

	const handleReset = () => {
		// Resetear cada campo manualmente
		handleChange('', 'nombre')
		handleChange('', 'jerarquiaPapaId')
		handleChange(null, 'archivo')

		// Resetear atributos
		setAtributosTipoIdsSeleccionados([])

		// Resetear errores y estado modificado
		setErroresValidacion({})
	}

	const validarDatos = () => {
		const camposRequeridos = ['nombre']
		const nuevosErrores = {}

		camposRequeridos.forEach(campo => {
			if (!data[campo] || data[campo].toString().trim() === '') {
				nuevosErrores[campo] = 'Este campo es requerido'
			} else {
				nuevosErrores[campo] = null
			}
		})

		setErroresValidacion(nuevosErrores)
		return Object.keys(nuevosErrores).filter(key => nuevosErrores[key] !== null).length === 0
	}

	const handleSeleccionImagen = (files) => {
		if (files && files.length > 0) {
			handleChange(files[0], 'archivo')
		}
	}

	const { data, handleChange, handleSubmit, reset, updateInitialData } = useForm(
		datosIniciales,
		() => true, // Validación básica (la hacemos manual con validarDatos)
		handleProcesar
	)

	// render
	return (
		<Offcanvas show={mostrar} onHide={handleClose} placement="end">
			<PanelHeader onClick={handleClose} iconClass="fa-arrow-left" btnText="Atrás" text="Nueva categoría" />
			<Offcanvas.Body id="nueva-categoria">
				<h5 className="mb-3">Campos de la categoría</h5>
				<Row>
					<Col sm={12} md={6}>
						<Form onSubmit={handleSubmit} className="ms-4">
							<Form.Group className="mb-3" controlId="nombre">
								<FormLabel>Nombre*</FormLabel>
								<Form.Control
									value={data.nombre}
									onChange={(e) => handleChange(e.target.value, 'nombre')}
									isInvalid={erroresValidacion.nombre}
								/>
								<Form.Control.Feedback type="invalid">{erroresValidacion.nombre}</Form.Control.Feedback>
							</Form.Group>

							<Form.Group className="mb-3" controlId="jerarquiaPapaId">
								<FormLabel>Categoría padre</FormLabel>
								<Form.Select
									value={data.jerarquiaPapaId}
									onChange={(e) => handleChange(e.target.value, 'jerarquiaPapaId')}
								>
									{opcionesCategorias.map((opcion) => (
										<option key={opcion.value} value={opcion.value}>
											{opcion.label}
										</option>
									))}
								</Form.Select>
								<Form.Text className="text-muted">
									Si no seleccionas una categoría padre, se creará como categoría principal.
								</Form.Text>
							</Form.Group>
						</Form>

						{/* NUEVA SECCIÓN: Atributos por defecto */}
						<div className="ms-4">
							<hr className="my-4" />

							<h5 className="mb-3">
								<i className="fa fa-sliders me-2"></i>
								Atributos por defecto
							</h5>

							<p className="text-muted small mb-3">
								Selecciona los tipos de atributos que los productos de esta categoría
								heredarán automáticamente (opcional).
							</p>

							<AtributosSelector
								atributosTiposDisponibles={atributosTiposDisponibles}
								atributosTipoIdsSeleccionados={atributosTipoIdsSeleccionados}
								onChange={setAtributosTipoIdsSeleccionados}
								disabled={loading}
								loading={loadingAtributos}
								itemsAfectados={0}
							/>
						</div>
					</Col>
					<Col sm={12} md={6}>
						<FileUpload
							title="Imagen de la categoría"
							accept=".png, .jpg, .jpeg"
							buttonText="Elegir"
							buttonTextSingular="imagen"
							updateFiles={handleSeleccionImagen}
						/>

						<Alert variant="info" className="mt-3">
							<i className="fa fa-info-circle me-2"></i>
							<small>
								La imagen es opcional. Formato recomendado: imagen cuadrada de al menos 300x300 píxeles.
							</small>
						</Alert>
					</Col>
				</Row>
			</Offcanvas.Body>
			<PanelFooter>
				<Button variant="outline-tertiary" onClick={handleClose} className="me-2" disabled={loading}>
					Cancelar
				</Button>
				<Button
					variant="primary"
					onClick={() => {
						if (validarDatos()) {
							handleSubmit()
						}
					}}
					disabled={loading}
				>
					{loading ? <><i className="fa fa-spinner fa-spin me-2"></i> Guardando...</> : 'Guardar'}
				</Button>
			</PanelFooter>
		</Offcanvas>
	)
}

export default NuevaCategoria